import { HttpClient } from '@angular/common/http';
import { Component, Input, forwardRef, ElementRef, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import Enumerable from 'linq';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AzureBlobSASViewModel } from '../_models/generatedModels';
import { ISasToken } from '../_services/azureStorage';
import { BlobStorageService } from '../_services/blob-storage.service';
import { FileService } from '../_services/generatedServices';

interface IUploadProgress {
  filename: string;
  progress: number;
}

//https://www.talkingdotnet.com/show-image-preview-before-uploading-using-angular-7/
//https://blog.angular-university.io/angular-file-upload/

@Component({
  selector: 'azure-file-upload',
  template: `
    <div class="upload-wrapper">
      <div *ngIf="percentComplete >= 0" class="upload-progress">
        <circle-progress [responsive]="true" [percent]="percentComplete" [startFromZero]="false" [radius]="100" [outerStrokeWidth]="16" [innerStrokeWidth]="8" [outerStrokeColor]="'#2e3f50'" [innerStrokeColor]="'#c6c6c6'" [animation]="true" [animationDuration]="300"></circle-progress>
      </div>
      <div *ngIf="imagePreview || value || existingFileName" class="img-tools text-center d-flex justify-content-center">
        <div class="toolbar">
          <button type="button" (click)="clearImage()" class="btn-lg my-auto mx-auto"><i class="fas fa-trash"></i></button>
          <button type="button" (click)="downloadFile()" class="btn-lg my-auto mx-auto"><i class="fas fa-download"></i></button>
        </div>
        <img [style.max-height.px]="maxDisplayHeight || null" class="img-fluid" *ngIf="value && !imagePreview && canPreview" [src]="sanitizedSrc" />
        <img [style.max-height.px]="maxDisplayHeight || null" class="img-fluid" *ngIf="imagePreview && canPreview" [src]="imagePreview" />
        <div class="my-auto mx-auto" *ngIf="!canPreview">
          <h1><i [ngClass]="mimeType?.icon" [style.color]="mimeType?.color"></i></h1>
          <h6>{{ existingFileName }}</h6>
        </div>
      </div>
      <div *ngIf="(!imagePreview && !value && !existingFileName) || invalidDimensionsError" fileDrop (fileDropped)="onFileDropped($event)" style="width: 100%; height: 100%; min-height: 200px; border: 2px dashed #ccc; border-radius: 20px" class="drop-zone d-flex">
        <div class="my-auto mx-auto">
          <input type="file" class="" [accept]="acceptedFileTypes" #fileDropRef id="fileDropRef" (change)="fileBrowseHandler($event)" />
          <h3><i class="fas fa-upload"></i></h3>
          <h3>drop file here</h3>
          <h3>or</h3>
          <button type="button" class="btn btn-admin-primary">Browse for file</button>
          <span *ngIf="requiredDimensions" class="d-block text-muted mt-2">(Required dimensions: {{ requiredDimensions }})</span>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AzureFileUploadControl),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AzureFileUploadControl),
      multi: true,
    },
  ],
})
export class AzureFileUploadControl implements ControlValueAccessor {
  value: string;
  @Input() azureContainer = 'images';
  @Input() requiredDimensions: string;
  @Input() isRequired: boolean;
  @Input() src: string;
  @Input() existingFileType: string;
  @Input() existingFileName: string;
  @Input() downloadFileName: string; //only used when existingFileName is not set
  @Input() immediateUpload: boolean;
  @Input() acceptedFileTypes: string[] = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
  @Input() maxDisplayHeight: number = null;
  @Output() uploadComplete: EventEmitter<any> = new EventEmitter();
  @Output() invalidFileType: EventEmitter<any> = new EventEmitter();

  mimeTypes = Enumerable.from([
    { name: 'image/png', canPreview: true, icon: 'fas fa-file-image', color: '#183153' },
    { name: 'image/jpeg', canPreview: true, icon: 'fas fa-file-image', color: '#183153' },
    { name: 'image/gif', canPreview: true, icon: 'fas fa-file-image', color: '#183153' },
    { name: 'image/svg+xml', canPreview: false, icon: 'fas fa-file-image', color: '#183153' },
    { name: 'image/vnd.adobe.photoshop', canPreview: false, icon: 'fas fa-file-image', color: '#183153' },
    { name: 'image/webp', canPreview: true, icon: 'fas fa-file-image', color: '#183153' },
    { name: 'image/bmp', canPreview: true, icon: 'fas fa-file-image', color: '#183153' },
    { name: 'image/tiff', canPreview: true, icon: 'fas fa-file-image', color: '#183153' },
    { name: 'application/pdf', canPreview: false, icon: 'fas fa-file-pdf', color: 'darkred' },
    { name: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', canPreview: false, icon: 'fas fa-file-word', color: '#183153' },
    { name: 'application/msword', canPreview: false, icon: 'fas fa-file-word', color: '#183153' },
    { name: 'application/postscript', canPreview: false, icon: 'fas fa-file-image', color: '#183153' },
    { name: 'video/mp4', canPreview: false, icon: 'fas fa-file-video', color: '#183153' },
    { name: 'video/quicktime', canPreview: false, icon: 'fas fa-file-video', color: '#183153' },
    { name: 'video/x-ms-wmv', canPreview: false, icon: 'fas fa-file-video', color: '#183153' },
    { name: 'video/x-flv', canPreview: false, icon: 'fas fa-file-video', color: '#183153' },
    { name: 'video/x-ms-asf', canPreview: false, icon: 'fas fa-file-video', color: '#183153' },
    { name: 'video/x-msvideo', canPreview: false, icon: 'fas fa-file-video', color: '#183153' },
    { name: 'video/webm', canPreview: false, icon: 'fas fa-file-video', color: '#183153' },
    { name: 'video/mpeg', canPreview: false, icon: 'fas fa-file-video', color: '#183153' },
    { name: 'application/vnd.ms-excel', canPreview: false, icon: 'fas fa-file-excel', color: '#1d6f42' },
    { name: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', canPreview: false, icon: 'fas fa-file-excel', color: '#1d6f42' },
  ]);

  selectedFile: any;
  sanitizedSrc: SafeUrl;
  canPreview = false;
  isNotImageError = false;
  invalidDimensionsError = false;
  imagePreview: string | ArrayBuffer | SafeUrl;
  dimensions: string;
  percentComplete: number;

  constructor(private http: HttpClient, elementRef: ElementRef, private blobStorage: BlobStorageService, private sanitizer: DomSanitizer, private fileService: FileService) {}

  propagateChange = (_: any) => {};
  propagateTouch = () => {};

  get mimeType() {
    return this.mimeTypes.firstOrDefault((x) => x.name === this.existingFileType);
  }

  writeValue(value: string): void {
    this.dimensions = this.requiredDimensions;

    if (this.existingFileType == null || this.mimeTypes.firstOrDefault((x) => x.name == this.existingFileType)?.canPreview) {
      this.canPreview = true;
      this.sanitizedSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.src);
    } else {
      this.canPreview = false;
    }

    this.value = value;
  }

  downloadFile() {
    if (this.selectedFile) {
      const objectUrl = URL.createObjectURL(this.selectedFile);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = this.existingFileName || this.downloadFileName;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } else {
      this.http.get(this.src, { responseType: 'blob' }).subscribe((result) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(result);
        a.href = objectUrl;
        a.download = this.existingFileName || this.downloadFileName;
        a.click();
        URL.revokeObjectURL(objectUrl);
      });
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  clearImage() {
    this.value = null;
    this.imagePreview = null;
    this.existingFileName = null;
    this.selectedFile = null;
    this.existingFileType = null;
    this.dimensions = this.requiredDimensions;
    this.propagateChange(this.value);
  }

  validate({ value }: FormControl) {
    this.invalidDimensionsError = this.requiredDimensions && this.dimensions != this.requiredDimensions;

    if (this.invalidDimensionsError) {
      return { invalidDimensions: true };
    }

    if (this.isNotImageError) {
      return { invalidFileType: true };
    }

    if (this.isRequired && !this.selectedFile && !this.value) {
      return { isRequired: true };
    }
  }

  onChange() {
    this.propagateChange(this.value);
    this.propagateTouch();
  }

  onTouch() {
    this.propagateTouch();
  }

  onFileDropped($event) {
    this.previewImage($event[0]);
  }

  public Upload(): Promise<any> {
    return new Promise<any>((resolve) => {
      let result = {} as any;
      result.name = this.value;
      result.url = null;
      result.filename = this.existingFileName || null;
      result.mimeType = this.existingFileType || null;

      if (!this.selectedFile) {
        resolve(result);
        return;
      }

      this.fileService.getSASForVideoContainer(this.selectedFile.name, this.azureContainer).subscribe((sasResult) => {
        this.uploadFile(this.selectedFile, sasResult).subscribe(
          (progress) => {
            if (progress.progress === 0) {
              this.percentComplete = 1;
            } else {
              this.percentComplete = progress.progress;
            }
          },
          (error) => {
            resolve(result);
            console.log('upload error', error);
          },
          () => {
            console.log('Upload complete');
            this.value = sasResult.fileName;
            this.percentComplete = -1;

            result.name = sasResult.fileName;
            result.url = sasResult.uri;
            result.filename = this.selectedFile.name;
            result.mimeType = this.selectedFile.type;
            this.propagateChange(this.value);
            resolve(result);
            this.uploadComplete.emit(result);
          }
        );
      });
    });
  }

  uploadFile(file: File, sasResult: AzureBlobSASViewModel): Observable<IUploadProgress> {
    const sasToken: ISasToken = {
      container: sasResult.containerName,
      filename: sasResult.fileName,
      storageAccessToken: sasResult.sasToken,
      storageUri: sasResult.storageUri,
    };

    return this.blobStorage.uploadToBlobStorage(sasToken, file).pipe(map((progress) => this.mapProgress(file, progress)));
  }

  private mapProgress(file: File, progress: number): IUploadProgress {
    return {
      filename: file.name,
      progress: progress,
    };
  }
  // public Upload(): Promise<string> {
  //   return new Promise<string>((resolve) => {
  //     if (this.selectedFile) {
  //       const formData = new FormData();
  //       formData.append('files', this.selectedFile);
  //       this.http.post(this.uploadUrl, formData).subscribe((result: any) => {
  //         this.value = result.name;
  //         result.filename = this.selectedFile.name;
  //         result.mimeType = this.selectedFile.type;
  //         this.propagateChange(this.value);
  //         resolve(this.value);
  //         this.uploadComplete.emit(result);
  //       });
  //     } else {
  //       resolve(this.value);
  //     }
  //   });
  // }

  previewImage(file: any) {
    var mimeType = file.type;

    if (this.acceptedFileTypes && this.acceptedFileTypes.length > 0 && !Enumerable.from(this.acceptedFileTypes).any((x) => x == mimeType)) {
      this.isNotImageError = true;
      this.onChange();
      console.log('Invalid file type');
      this.invalidFileType.emit(file);
      return;
    } else {
      this.isNotImageError = false;
    }

    this.selectedFile = file;
    var reader = new FileReader();
    reader.readAsDataURL(this.selectedFile);
    var parent = this;
    reader.onload = (_event) => {
      this.existingFileType = file.type;
      this.existingFileName = file.name;

      this.mimeTypes.firstOrDefault((x) => x.name == file.type)?.canPreview;

      if (this.mimeTypes.firstOrDefault((x) => x.name == file.type)?.canPreview) {
        this.canPreview = true;
        var img = new Image();
        img.onload = () => {
          parent.dimensions = `${img.width}x${img.height}`;
          console.log(parent.dimensions);
          console.log(img.width + 'x' + img.height);
          parent.onChange();

          this.invalidDimensionsError  = parent.requiredDimensions && parent.dimensions != parent.requiredDimensions;
          if (this.invalidDimensionsError) {
            console.log('invalid dimensions');
            console.log(parent.requiredDimensions);
            console.log(parent.dimensions);
            img.src = '';
            parent.imagePreview = '';
            return null;
          }

          if (this.immediateUpload) {
            this.Upload();
          }
        };
        img.src = <any>reader.result;
        this.imagePreview = this.sanitizer.bypassSecurityTrustResourceUrl(<string>_event.target.result);
      } else {
        this.dimensions = this.requiredDimensions;
        this.canPreview = false;
        parent.onChange();
        if (this.immediateUpload) {
          this.Upload();
        }
      }
    };
  }

  fileBrowseHandler($event) {
    var file = $event.target.files[0];
    this.previewImage(file);
  }
}
